/**
 * <coating-overview-el>
 *
 * Initializes a GLightbox gallery scoped to this section.
 * All gallery anchors inside the element share one `data-gallery` group,
 * so clicking any preview image (or the "open gallery" button) opens the
 * full set with prev/next navigation, keyboard arrows, ESC and touch/zoom.
 */
class CoatingOverview extends HTMLElement {
  /** @type {any} */
  #lightbox = null;

  /** @type {HTMLElement | null} */
  #openButton = null;

  connectedCallback() {
    this.#init();

    document.addEventListener(
      'shopify:section:load',
      (event) => {
        if (/** @type {HTMLElement} */ (event.target).contains(this)) {
          this.#destroy();
          this.#init();
        }
      },
      { once: true }
    );
  }

  disconnectedCallback() {
    this.#destroy();
  }

  #init() {
    const Lightbox = /** @type {any} */ (window).GLightbox;
    if (typeof Lightbox !== 'function') {
      // GLightbox script may still be loading — retry once it is ready.
      const script = document.querySelector('script[src*="glightbox"]');
      if (script) {
        script.addEventListener('load', () => this.#init(), { once: true });
      }
      return;
    }

    const selector = `#${this.dataset.galleryId} a.coating-overview__lightbox`;
    if (!document.querySelector(selector)) return;

    this.#lightbox = Lightbox({
      selector,
      touchNavigation: true,
      keyboardNavigation: true,
      loop: true,
      zoomable: true,
      draggable: true,
      openEffect: 'fade',
      closeEffect: 'fade',
      slideEffect: 'slide',
    });

    // "Open gallery" button(s) open the lightbox at the first image.
    this.#openButton = /** @type {HTMLElement | null} */ (
      this.querySelector('.coating-overview__open')
    );
    if (this.#openButton) {
      this.#openButton.addEventListener('click', this.#handleOpenClick);
    }
  }

  #handleOpenClick = (event) => {
    event.preventDefault();
    this.#lightbox?.openAt(0);
  };

  #destroy() {
    this.#openButton?.removeEventListener('click', this.#handleOpenClick);
    this.#openButton = null;
    this.#lightbox?.destroy();
    this.#lightbox = null;
  }
}

if (!customElements.get('coating-overview-el')) {
  customElements.define('coating-overview-el', CoatingOverview);
}
